"use client";

import { useResponsive } from "@dafiti/closet-ui/hooks";
import { useCallback, useState } from "react";

import { catalogFilterClick } from "../tracking/catalogPage.tracking";
import type { Facet } from "../types/filters.type";
import { filterParsers, useFilterParams } from "./useFilterParams.hook";
import type { FilterParamKey } from "./useFilterParams.hook";

type SetFilterParams = ReturnType<typeof useFilterParams>[1];
type FilterParamUpdate = Parameters<SetFilterParams>[0];

// ─── resolvers puros (sem hook, sem state) ───────────────────────────────────

function isPriceFacet(facet: Facet): boolean {
  return facet.component === "CustomPrice";
}

function buildSelectionsFromParams(
  facet: Facet,
  filterParams: Record<string, unknown>,
): string[] {
  if (isPriceFacet(facet)) {
    const min = filterParams.price_min;
    const max = filterParams.price_max;
    if (min != null || max != null)
      return [String(min ?? ""), String(max ?? "")];
    return [];
  }

  const raw = filterParams[facet.field];
  if (Array.isArray(raw)) return raw as string[];
  return [];
}

function buildParamsUpdate(
  facet: Facet,
  selections: string[],
): FilterParamUpdate | null {
  if (isPriceFacet(facet)) {
    const [min, max] = selections;
    return {
      price_min: min && min !== "" ? Number(min) : null,
      price_max: max && max !== "" ? Number(max) : null,
    } as FilterParamUpdate;
  }

  const key = facet.field as FilterParamKey;
  if (!(key in filterParsers)) return null;

  return {
    [key]: selections.length > 0 ? selections : null,
  } as FilterParamUpdate;
}

// ─── hook ────────────────────────────────────────────────────────────────────

export function useQuickFilterTrigger(facet: Facet) {
  const [isOpen, setIsOpen] = useState(false);
  const [filterParams, setFilterParams] = useFilterParams();
  const [initialSelections, setInitialSelections] = useState<string[]>([]);
  const containerId = `quick-filter-container-${facet.field}`;

  const { isMobile } = useResponsive();
  const isDesktop = isMobile === false;

  const open = useCallback(() => {
    const initial = buildSelectionsFromParams(
      facet,
      filterParams as Record<string, unknown>,
    );
    setInitialSelections(initial);
    setIsOpen(true);
  }, [facet, filterParams]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const applyFilter = useCallback(
    (selections: string[]) => {
      const update = buildParamsUpdate(facet, selections);
      if (update) setFilterParams(update);
      const hasActiveSelection = selections.some(
        (selection) => selection !== "" && selection != null,
      );
      if (hasActiveSelection) catalogFilterClick([facet.field]);
      setIsOpen(false);
    },
    [facet, setFilterParams],
  );

  return {
    isOpen,
    isDesktop,
    open,
    close,
    applyFilter,
    containerId,
    initialSelections,
  };
}
